import { Text } from '@avanta/shared/components/Text';
import { cn } from '@avanta/shared/utils/cn';
import { ChevronRight, IdCard, Mail, Phone } from 'lucide-react';
import { type Employee } from 'types/employees';
import { PatientInfoRow } from './PatientInfoRow';

interface PatientCardProps {
  patient: Employee;
  onSelect?: (patient: Employee) => void;
}

const CARD_CLASS_NAME =
  'flex w-full shrink-0 items-center gap-4 overflow-hidden rounded-xl border border-teal-300 bg-white py-4 pr-4 pl-6';

export const PatientCard = ({ patient, onSelect }: PatientCardProps): React.ReactElement => {
  const fullName = [patient.firstName, patient.surname].filter(Boolean).join(' ') || patient.email;

  const content = (
    <>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Text variant="text-md-medium" className="text-denim-800">
          {fullName}
        </Text>
        <div className="grid grid-cols-2 gap-4">
          {patient.email && <PatientInfoRow icon={Mail} value={patient.email} />}
          {patient.contactPhone && <PatientInfoRow icon={Phone} value={patient.contactPhone} />}
          {patient.nif && <PatientInfoRow icon={IdCard} value={patient.nif} />}
        </div>
      </div>
      {onSelect && (
        <ChevronRight aria-hidden className="size-8 shrink-0 text-teal-600" strokeWidth={1.5} />
      )}
    </>
  );

  if (!onSelect) {
    return <div className={CARD_CLASS_NAME}>{content}</div>;
  }

  const handleClick = (): void => {
    onSelect(patient);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        CARD_CLASS_NAME,
        'text-left transition-colors hover:bg-plum-100 hover:shadow-box-sm hover:border-transparent'
      )}
    >
      {content}
    </button>
  );
};
